/* eslint-disable no-unused-vars */
import jwt from "@/api/jwt.js"
import profileApi from "@/api/profile.js"
import user from "@/api/user.js"
import companyApi from "@/api/company.js"
import router from "@/router"

async function loginWithAuthResponse(commit, { accessToken, userData }, next) {
  const user = userData

  commit("SET_TOKEN", { accessToken }, { root: true })
  localStorage.setItem("GFB_ACCESS_TOKEN", accessToken)
  commit(
    "UPDATE_USER_INFO",
    {
      ...user,
      profilePhoto:
        user.profilePhoto ||
        require("@/assets/images/portrait/small/sample-avatar.jpg"),
    },
    { root: true }
  )
  commit("SET_BEARER", accessToken)

  const { data } = await profileApi.checkUserProfileExists(user._id)
  const { data: companyData } = await companyApi.checkUserProfileExists(
    user._id
  )
  const profile = data?.profile ?? {}
  let companyProfile = companyData?.profile ?? {}
  let profileExists = Boolean(profile || companyProfile)
  let profileType = localStorage.getItem("profileType")

  if (companyProfile && user?.companyId) {
    const companyProfileData = await companyApi.getProfile(user.companyId)
    const companyProfileUser = companyProfileData.data.user
    const companyProfileResult = await companyApi.getUserProfile(
      companyProfileUser
    )
    companyProfile = companyProfileResult.data
    profileExists = Boolean(companyProfile)
    profileType = "provider"
  }

  if (profileExists) {
    let profileData
    if (user?.companyId && companyProfile) {
      profileData = companyProfile
      profileType = "provider"
    } else {
      const res = profile
        ? await profileApi.getUserProfile(user._id)
        : await companyApi.getUserProfile(user._id)
      profileData = res.data
    }
    if (profileData) {
      commit("UPDATE_USER_PROFILE", profileData, { root: true })
    }
  }

  const defaultTo =
    user.role === "support"
      ? "/admin/dashboard"
      : profileExists
      ? "/dashboard"
      : {
          path: "/complete-profile",
          ...(profileType ? { query: { profileType } } : {}),
        }

  router.push(next || defaultTo)
}

export default {
  registerUser({ dispatch }, payload) {},
  async requestPasswordReset(_, payload) {
    return await user.requestPasswordReset(payload.email)
  },
  async changeUserPassword(_, payload) {
    if (payload.authVerified) {
      return await user.changeAuthUserPassword(payload.password)
    } else {
      return await user.changeUserPassword(
        payload.passwordResetKey,
        payload.password
      )
    }
  },
  async checkUserPassword(_, payload) {
    return await user.checkUserPassword(payload.password)
  },
  // JWT
  login({ commit }, payload) {
    return new Promise((resolve, reject) => {
      jwt
        .login(
          payload.userDetails.email.trim(),
          payload.userDetails.password.trim()
        )
        .then(async (response) => {
          const { accessToken } = response.data ?? {}
          const user = response.data?.userData
          // If there's user data in response
          if (user) {
            // Set accessToken
            commit("SET_TOKEN", { accessToken }, { root: true })
            localStorage.setItem("GFB_ACCESS_TOKEN", accessToken)

            commit(
              "UPDATE_USER_INFO",
              {
                ...user,
                profilePhoto:
                  user.profilePhoto ||
                  require("@/assets/images/portrait/small/sample-avatar.jpg"),
              },
              {
                root: true,
              }
            )

            // Set bearer token in axios
            commit("SET_BEARER", accessToken)

            resolve(response)

            // Navigate User to homepage

            const { data } = await profileApi.checkUserProfileExists(user._id)
            const { data: companyData } =
              await companyApi.checkUserProfileExists(user._id)

            const profile = data?.profile ?? {}
            let companyProfile = companyData?.profile ?? {}
            const currentRoute = router.currentRoute.value
            let profileExists = Boolean(profile || companyProfile)
            let profileType = localStorage.getItem("profileType")

            if (companyProfile && user?.companyId) {
              let companyProfileData = await companyApi.getProfile(
                user.companyId
              )
              let companyProfileUser = companyProfileData.data.user
              let companyProfileResult = await companyApi.getUserProfile(
                companyProfileUser
              )
              companyProfile = companyProfileResult.data
              profileExists = Boolean(companyProfile)
              profileType = "provider"
            }

            if (!profileExists && user) {
              if (user?.companyId) {
                let companyProfileData = await companyApi.getProfile(
                  user.companyId
                )
                let companyProfileUser = companyProfileData.data.user
                let companyProfileResult = await companyApi.getUserProfile(
                  companyProfileUser
                )
                companyProfile = companyProfileResult.data
                profileExists = Boolean(companyProfile)
                profileType = "provider"
              }
            }

            if (profileExists) {
              let data
              if (user?.companyId && companyProfile) {
                data = companyProfile
                profileType = "provider"
              } else {
                let profileData = profile
                  ? await profileApi.getUserProfile(user._id)
                  : await companyApi.getUserProfile(user._id)
                data = profileData.data
              }
              // lastActivity is recorded by the API at login and on every
              // token refresh (GFB-1408) -- no profile PATCH from here.
              if (data) {
                commit("UPDATE_USER_PROFILE", data, { root: true })
              }
            }

            const redirectPath = currentRoute.query?.redirect || "/dashboard"

            const to =
              user.role == "support"
                ? "/admin/dashboard"
                : profileExists
                ? redirectPath
                : {
                    path: "/complete-profile",
                    ...(profileType ? { query: { profileType } } : {}),
                  }

            router.push(to)
          } else {
            reject({ message: "Wrong Email or Password" })
          }
        })
        .catch((error) => {
          if (error.response?.data === "Incorrect username or password") {
            reject({ message: "Wrong Email or Password" })
          }
          const responseData = error.response?.data
          if (responseData?.code === "account_unclaimed") {
            reject({ code: "account_unclaimed", message: responseData.message })
            return
          }
          reject({ message: "Something went wrong" })
        })
    })
  },
  async claimAccount({ commit }, { token, password, next }) {
    const response = await jwt.claimAccount(token, password)
    const { accessToken } = response.data ?? {}
    const user = response.data?.userData
    if (!user) throw new Error("Claim failed")

    await loginWithAuthResponse(commit, { accessToken, userData: user }, next)
    return response
  },
  async googleAuth({ commit }, { idToken, profileType, company }) {
    if (profileType) {
      localStorage.setItem("profileType", profileType)
    }
    const response = await jwt.googleAuth(idToken, profileType, company)
    const {
      accessToken,
      userData: user,
      email,
      requiresVerification,
    } = response.data ?? {}

    if (!requiresVerification && accessToken && user) {
      // Returning user whose Google identity was already verified in a
      // prior signup/merge — log straight in, same as a normal login.
      await loginWithAuthResponse(commit, { accessToken, userData: user })
      return { loggedIn: true }
    }

    return { loggedIn: false, email }
  },
  async verifyGoogleCode({ commit }, { email, code, next }) {
    const response = await jwt.verifyGoogleCode(email, code)
    const { accessToken } = response.data ?? {}
    const user = response.data?.userData
    if (!user) throw new Error("Verification failed")

    await loginWithAuthResponse(commit, { accessToken, userData: user }, next)
    return response
  },
  async resendGoogleCode(_, { email }) {
    return await jwt.resendGoogleCode(email)
  },
  registerUserJWT({ commit }, payload) {
    const { displayName, email, password, confirmPassword } =
      payload.userDetails

    return new Promise((resolve, reject) => {
      // Check confirm password
      if (password !== confirmPassword) {
        reject({
          message: "Password doesn't match. Please try again.",
        })
      }

      jwt
        .registerUser(displayName, email, password)
        .then((response) => {
          const { accessToken, refreshToken, userData } = response?.data ?? {}

          commit("SET_TOKEN", { accessToken }, { root: true })
          commit("UPDATE_USER_INFO", userData, {
            root: true,
          })

          resolve(response)

          // Redirect User
          router.push(router.currentRoute.value.query?.to || "/")
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  registerUserEmail({ commit }, payload) {
    return new Promise((resolve, reject) => {
      user
        .registerUser(payload.userDetails)
        .then((response) => {
          // show message to user
          resolve(response)
          // Redirect User
          //router.push(router.currentRoute.query.to || '/')
        })
        .catch((error) => {
          reject(error)
          //console.log(error);
          //reject({message: 'Email address not available'})
        })
    })
  },
  async confirmUserEmail({ commit }, payload) {
    const { emailConfirmationKey, email, code, password } = payload.userDetails
    const details = emailConfirmationKey
      ? { emailConfirmationKey, password }
      : { email, code, password }
    try {
      const { data } = await user.confirmUserEmail(details)
      return data
    } catch (error) {
      console.error(error)
      // A wrong/expired code (GFB-1412) comes back as a 400 with a message
      // worth showing; anything else keeps the generic text.
      if (!emailConfirmationKey && error.response?.status === 400) {
        throw new Error(
          error.response.data?.errors?.[0] || "Invalid or expired code."
        )
      }
      throw new Error("We couldn't confirm your account.")
    }
  },
  // The API records lastActivity on refresh (GFB-1408). This used to PATCH
  // the whole stored profile back after every refresh; a 403 on that PATCH
  // triggered another refresh, and so on forever. It also never settled
  // when the refresh call failed, leaving queued requests hanging -- now
  // it rejects so the interceptor can give up cleanly.
  refreshToken({ commit }) {
    return jwt.refreshToken().then((response) => {
      const { accessToken } = response.data ?? {}
      if (accessToken) {
        localStorage.setItem("GFB_ACCESS_TOKEN", accessToken)
        commit("SET_TOKEN", { accessToken }, { root: true })
      }
      return response
    })
  },
  async revokeToken({ commit }) {
    await jwt.revokeToken()
    commit("SET_TOKEN", { accessToken: null }, { root: true })
    commit("CLEAR_USER_INFO", {}, { root: true })
    commit("CLEAR_USER_PROFILE", {}, { root: true })
    return
  },
}
